import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Users, Mail, Edit, Trash, Send } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { toast } from "sonner";
import { ListToolbar } from "@/components/common/ListToolbar";
import { DataPagination } from "@/components/common/DataPagination";
import { LoadingState } from "@/components/common/LoadingState";
import { ErrorState } from "@/components/common/ErrorState";
import { EmptyState } from "@/components/common/EmptyState";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { queryKeys } from "@/lib/queryKeys";
import { useUrlState } from "@/lib/urlState";
import { exportRowsAsCsv } from "@/lib/csv";

interface StudentList {
  id: string;
  list_name: string;
  description: string | null;
  created_at: string;
  instructor_student_emails: Array<{
    id: string;
    email: string;
    student_name: string;
  }>;
}

export default function InstructorStudentLists() {
  const { user } = useAuth();
  const url = useUrlState();
  const queryClient = useQueryClient();
  const search = url.get("q") ?? "";

  const [selectedList, setSelectedList] = useState<StudentList | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmails, setInviteEmails] = useState([{ email: "", name: "" }]);

  const paginated = usePaginatedQuery<StudentList>({
    queryKey: queryKeys.studentLists.list({
      page: 0,
      pageSize: 12,
      filters: { search, userId: user?.id },
    }),
    table: "instructor_student_lists",
    select: "*, instructor_student_emails (id, email, student_name)",
    orderBy: { column: "created_at", ascending: false },
    initialPageSize: 25,
    syncToUrl: true,
    enabled: !!user,
    filters: (q) => {
      let next = q.eq("instructor_id", user!.id);
      if (search) next = next.or(`list_name.ilike.%${search}%,description.ilike.%${search}%`);
      return next;
    },
  });

  useEffect(() => {
    paginated.setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.studentLists.all });

  const handleDeleteList = async (listId: string) => {
    try {
      const { error } = await supabase.from("instructor_student_lists").delete().eq("id", listId);
      if (error) throw error;
      toast.success("Student list deleted successfully");
      refresh();
    } catch (error) {
      console.error("Error deleting list:", error);
      toast.error("Failed to delete student list");
    }
  };

  const handleAddEmailToList = () => setInviteEmails((prev) => [...prev, { email: "", name: "" }]);
  const handleRemoveEmail = (index: number) => {
    if (inviteEmails.length > 1) setInviteEmails((prev) => prev.filter((_, i) => i !== index));
  };
  const handleEmailChange = (index: number, field: "email" | "name", value: string) => {
    setInviteEmails((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const handleInviteStudents = async () => {
    if (!selectedList) return;
    const validEmails = inviteEmails.filter((item) => item.email.trim() && item.name.trim());
    if (validEmails.length === 0) {
      toast.error("Please enter at least one valid email and name");
      return;
    }
    try {
      const newStudents = validEmails.map((item) => ({
        list_id: selectedList.id,
        email: item.email.trim().toLowerCase(),
        student_name: item.name.trim(),
      }));
      const { error } = await supabase.from("instructor_student_emails").insert(newStudents);
      if (error) throw error;
      toast.success("Students added to list successfully!");
      setShowInviteDialog(false);
      setInviteEmails([{ email: "", name: "" }]);
      setSelectedList(null);
      refresh();
    } catch (error) {
      console.error("Error adding students:", error);
      toast.error("Failed to add students to list");
    }
  };

  const handleExport = () => {
    const rows = paginated.rows.map((l) => ({
      id: l.id,
      list_name: l.list_name,
      description: l.description ?? "",
      students: l.instructor_student_emails?.length ?? 0,
      created_at: l.created_at,
    }));
    exportRowsAsCsv(`student-lists-page-${paginated.page + 1}.csv`, rows);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Student Lists</h1>
            <p className="text-muted-foreground">Manage your student groups and invite students to tests</p>
          </div>
          <Button asChild>
            <Link to="/instructor/students/create-list">
              <Plus className="h-4 w-4 mr-2" />
              Create New List
            </Link>
          </Button>
        </div>

        <ListToolbar
          search={search}
          onSearchChange={(v) => url.set({ q: v || null })}
          searchPlaceholder="Search lists…"
          onExportCsv={handleExport}
        />

        <div className="mt-4">
          {paginated.isError ? (
            <ErrorState onRetry={paginated.refetch} />
          ) : paginated.isLoading && paginated.rows.length === 0 ? (
            <LoadingState />
          ) : paginated.rows.length === 0 ? (
            <EmptyState
              title={paginated.total === 0 ? "No Student Lists" : "No lists match your search"}
              description="Create your first student list to start inviting students to your tests."
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginated.rows.map((list) => (
                <Card key={list.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{list.list_name}</CardTitle>
                        {list.description && (
                          <CardDescription className="mt-1">{list.description}</CardDescription>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Edit List: {list.list_name}</DialogTitle>
                            </DialogHeader>
                            <p className="text-sm text-muted-foreground">
                              Edit functionality will be added in a future update.
                            </p>
                          </DialogContent>
                        </Dialog>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteList(list.id)}>
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">
                          {list.instructor_student_emails?.length ?? 0} students
                        </span>
                      </div>
                      <Badge variant="outline">{new Date(list.created_at).toLocaleDateString()}</Badge>
                    </div>

                    {(list.instructor_student_emails?.length ?? 0) > 0 && (
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground">STUDENTS</Label>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {list.instructor_student_emails.slice(0, 3).map((student) => (
                            <div key={student.id} className="flex items-center gap-2 text-xs">
                              <Mail className="h-3 w-3 text-muted-foreground" />
                              <span className="font-medium">{student.student_name}</span>
                              <span className="text-muted-foreground">({student.email})</span>
                            </div>
                          ))}
                          {list.instructor_student_emails.length > 3 && (
                            <div className="text-xs text-muted-foreground">
                              +{list.instructor_student_emails.length - 3} more students
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedList(list);
                          setShowInviteDialog(true);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Students
                      </Button>
                      <Button variant="outline" size="sm">
                        <Send className="h-4 w-4 mr-1" />
                        Invite to Test
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <DataPagination
            page={paginated.page}
            pageSize={paginated.pageSize}
            total={paginated.total}
            pageCount={paginated.pageCount}
            onPageChange={paginated.setPage}
            onPageSizeChange={paginated.setPageSize}
            isFetching={paginated.isFetching}
          />
        </div>

        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add Students to {selectedList?.list_name}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {inviteEmails.map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-3 border rounded">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Student Name</Label>
                      <Input
                        placeholder="Enter name"
                        value={item.name}
                        onChange={(e) => handleEmailChange(index, "name", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Email Address</Label>
                      <Input
                        type="email"
                        placeholder="Enter email"
                        value={item.email}
                        onChange={(e) => handleEmailChange(index, "email", e.target.value)}
                      />
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveEmail(index)}
                    disabled={inviteEmails.length === 1}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              <div className="flex justify-between">
                <Button variant="outline" onClick={handleAddEmailToList}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Another Student
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleInviteStudents}>Add Students</Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
      <Footer />
    </div>
  );
}
